/**
 * What `GraphEdit`'s setters store when a scene's properties apply in file order, before the node
 * is parented (`scene/resources/packed_scene.cpp:492`, `:541`). Three setters read state an earlier
 * key wrote, so an order-free property bag cannot recover the stored value. `graphEdit.md` walks
 * each setter through the engine lines that decide it.
 *
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 * See THIRD-PARTY-NOTICES.md.
 */
import { parseOptionalFloat, parseOptionalVector2 } from '../../../../parser/valueParsers';
// The min branch is tested first, so the inverted range this replay builds returns the MAX.
import { clamp } from '../../../../godot/math/math';
/**
 * `zoom_step`'s own member default (`graph_edit.h:227`), the only input to the
 * two bounds below. A `float`, so `Math::pow`'s float overload is the one the
 * constructor picks and the exponentiation starts from 1.2f, not from 1.2.
 */
const DEFAULT_ZOOM_STEP = Math.fround(1.2);
/** `zoom_min = 1 / Math::pow(zoom_step, 8)` (`graph_edit.cpp:3175`), stored in a `float`. */
const GRAPH_EDIT_DEFAULT_ZOOM_MIN = Math.fround(1 / Math.fround(Math.pow(DEFAULT_ZOOM_STEP, 8)));
/** `zoom_max = 1 * Math::pow(zoom_step, 4)` (`graph_edit.cpp:3177`), stored in a `float`. */
const GRAPH_EDIT_DEFAULT_ZOOM_MAX = Math.fround(Math.pow(DEFAULT_ZOOM_STEP, 4));
/** `zoom`'s own member default (`graph_edit.h:226`). */
const DEFAULT_ZOOM = 1;
/** `min_scroll_offset` and `max_scroll_offset` as GraphEdit's constructor leaves them. */
const ZERO_SCROLL_BOUNDS = { x: 0, y: 0 };
/**
 * `Control::get_size()` as `_size_changed` leaves it out of tree (`control.cpp:1765-1797`): the
 * parent rect is empty there (`:687-689`), so each `anchor_*` adds zero and the offsets span the
 * size, floored by `custom_minimum_size` (`:1744-1750`).
 */
function sizeOf(state) {
    return {
        x: Math.max(state.offsets[2] - state.offsets[0], state.customMinimum.x),
        y: Math.max(state.offsets[3] - state.offsets[1], state.customMinimum.y),
    };
}
/**
 * `GraphEdit::_update_scrollbars` (`graph_edit.cpp:488-493`) over the empty
 * child list a load presents it with: the merged rect is a point at the origin,
 * grown by one size in every direction. It queues `_update_scroll_offset` (`:524`).
 */
function installScrollBounds(state) {
    state.queuedScrollOffsetUpdate = true;
    const size = sizeOf(state);
    state.scrollMin = { x: -size.x, y: -size.y };
    state.scrollMax = { x: size.x, y: size.y };
}
/** `GraphEdit::set_zoom_custom` (`graph_edit.cpp:2431-2448`), the parts a load reaches: `is_visible_in_tree()` is false, so the scroll-anchor rewrite at `:2452-2453` never runs. */
function applySetZoom(state, requested) {
    const zoom = clamp(requested, state.zoomMin, state.zoomMax);
    state.zoomTouched = true;
    // An unchanged zoom returns before the buttons' disabled flags (`:2435-2437`), so a bound
    // written after `zoom` moves neither.
    if (state.zoom === zoom)
        return;
    state.zoom = zoom;
    state.minusDisabled = zoom === state.zoomMin;
    state.plusDisabled = zoom === state.zoomMax;
    installScrollBounds(state);
}
const OFFSET_KEYS = {
    offset_left: 0,
    offset_top: 1,
    offset_right: 2,
    offset_bottom: 3,
};
/**
 * Replays the keys in file order, as `Label`'s `resolveVisibleChars` does (`../label/parser.ts`):
 * the raw property bag's insertion order is file order (`TscnParserCore.ts`). `anchors_preset` and
 * `layout_mode` are not modelled (`graphEdit.md`).
 */
export function resolveGraphEditLoadState(properties) {
    const state = {
        offsets: [0, 0, 0, 0],
        customMinimum: { x: 0, y: 0 },
        zoom: DEFAULT_ZOOM,
        zoomMin: GRAPH_EDIT_DEFAULT_ZOOM_MIN,
        zoomMax: GRAPH_EDIT_DEFAULT_ZOOM_MAX,
        zoomTouched: false,
        minusDisabled: false,
        plusDisabled: false,
        scrollMin: ZERO_SCROLL_BOUNDS,
        scrollMax: ZERO_SCROLL_BOUNDS,
        scrollOffset: undefined,
        queuedScrollOffsetUpdate: false,
    };
    for (const key of Object.keys(properties)) {
        const side = OFFSET_KEYS[key];
        if (side !== undefined) {
            const value = parseOptionalFloat(properties[key]);
            if (value !== undefined)
                state.offsets[side] = value;
            continue;
        }
        switch (key) {
            case 'custom_minimum_size': {
                const value = parseOptionalVector2(properties.custom_minimum_size);
                if (value)
                    state.customMinimum = value;
                break;
            }
            case 'scroll_offset': {
                const value = parseOptionalVector2(properties.scroll_offset);
                if (!value)
                    break;
                // `p_offset.clamp(min_scroll_offset, max_scroll_offset - get_size())` (`:407`),
                // per component (`core/math/vector2.cpp:132-136`).
                const size = sizeOf(state);
                state.scrollOffset = {
                    x: clamp(value.x, state.scrollMin.x, state.scrollMax.x - size.x),
                    y: clamp(value.y, state.scrollMin.y, state.scrollMax.y - size.y),
                };
                // `set_scroll_offset` queues `_update_scroll_offset` (`:409`). Its rerun of
                // `_update_scrollbars` after the clamp (`:414`) writes bounds that no later key reads.
                state.queuedScrollOffsetUpdate = true;
                break;
            }
            case 'zoom': {
                const value = parseOptionalFloat(properties.zoom);
                if (value !== undefined)
                    applySetZoom(state, value);
                break;
            }
            case 'zoom_min': {
                const value = parseOptionalFloat(properties.zoom_min);
                // `ERR_FAIL_COND_MSG(p_zoom_min > zoom_max)` (`:2480`): the property is refused outright.
                if (value === undefined || value > state.zoomMax || value === state.zoomMin)
                    break;
                state.zoomMin = value;
                applySetZoom(state, state.zoom);
                break;
            }
            case 'zoom_max': {
                const value = parseOptionalFloat(properties.zoom_max);
                // `ERR_FAIL_COND_MSG(p_zoom_max < zoom_min)` (`:2495`).
                if (value === undefined || value < state.zoomMin || value === state.zoomMax)
                    break;
                state.zoomMax = value;
                applySetZoom(state, state.zoom);
                break;
            }
            default:
                break;
        }
    }
    return {
        scrollOffset: state.scrollOffset,
        zoom: state.zoomTouched ? state.zoom : undefined,
        zoomMinusDisabled: state.minusDisabled,
        zoomPlusDisabled: state.plusDisabled,
        queuedScrollOffsetUpdate: state.queuedScrollOffsetUpdate,
    };
}
//# sourceMappingURL=loadOrder.js.map